<template>
  <div class="view page" data-app>
    <management-page-back-arrow
      v-if="backButton"
      @buttonClick="$router.back()"
    />
    <management-page-back-arrow
      v-if="customNavigation"
      @buttonClick="
        $router.push({
          name: pageName,
          params: { uuid: uuid }
        })
      "
    />

    <v-layout
      row
      justify-space-between
      align-start
      wrap
      class="title-section"
      v-if="!loading"
    >
      <v-flex xs12 md5 lg3>
        <v-layout column class="page-header">
          <span class="page-title">
            {{ title }}
          </span>

          <div class="buttons">
            <slot name="buttons" />

            <view-page-button
              v-if="editButton"
              @click="editItem"
              :label="$t('Edit')"
              icon="edit"
              :disabled="editDisabled"
            />

            <view-page-button
              v-if="deleteButton"
              @click="toggleDeleteDialog(true)"
              :label="$t('Delete')"
              icon="delete"
              :disabled="deleteDisabled"
            />

          </div>
        </v-layout>

        <div class="page-section" style="padding: 0">
          <img
            v-if="hasImage"
            :src="image"
            alt="Image icon"
            class="data-image"
          />

          <div class="data-fields" style="padding: 40px">
            <slot name="leftSide" />
          </div>
        </div>
      </v-flex>

      <v-flex xs12 md6 lg8>
        <div class="page-header">
          <slot name="headerRightSide" />
        </div>

        <slot name="rightSide" />
      </v-flex>
    </v-layout>

    <app-dialog-box
      v-if="deleteButton"
      :open="dialog"
      :title="deleteDialogTitle"
      :content="deleteDialogContent"
      :main-action-label="$t('No')"
      @mainAction="toggleDeleteDialog(false)"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="deleteItem"
    />
    <loading-spinner
      :active.sync="loading"
      :is-full-page="false"
      color="accent"
    />
  </div>
</template>

<script>
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import ManagementPageBackArrow from "../ManagementPageBackArrow";
import ViewPageButton from "./ViewPageButton";
import EditIcon from "@/assets/management/update.svg";
import DeleteIcon from "@/assets/management/delete.svg";
import AppDialogBox from "../../_Molecules/AppDialogBox";
import LoadingSpinner from "vue-loading-overlay";

export default {
  name: "ViewPage",

  components: {
    AppDialogBox,
    ViewPageButton,
    ManagementPageBackArrow,
    EditIcon,
    DeleteIcon,
    LoadingSpinner
  },

  mixins: [checkPermissionsMixin],

  props: {
    /**
     * Route Name
     */
    pageName: {
      type: String,
      required: false
    },
    /**
     * Page UUID to use as params
     */
    uuid: {
      type: String,
      required: false
    },
    /**
     * Title of the page.
     */
    title: {
      type: String,
      required: true
    },
    /**
     * Name of the permission of the current type of item.
     */
    permissionName: {
      type: String,
      required: true
    },
    /**
     * If the page should include an image from the data of the item.
     */
    hasImage: {
      type: Boolean,
      default: false
    },
    /**
     * Title of the page.
     */
    image: {
      type: String,
      required: false
    },
    /**
     * Title of the delete dialog box.
     */
    deleteDialogTitle: {
      type: String
    },
    /**
     * Map of the delete dialog box.
     */
    deleteDialogContent: {
      type: String
    },

    editButton: {
      type: Boolean,
      default: true
    },

    deleteButton: {
      type: Boolean,
      default: true
    },

    backButton: {
      type: Boolean,
      default: true
    },
    customNavigation: {
      type: Boolean,
      default: false
    },
    role: {
      type: String,
      default: "management"
    },

    /**
     * If the content of the page is loading or not.
     */
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      dialog: false
    };
  },

  computed: {
    deleteDisabled() {
      if (this.role === "management") {
        return !this.userHasManagementPermission(
          this.permissionName,
          "destroy"
        );
      } else if (this.role === "fibercloud") {
        return !this.userHasFibercloudPermission(
          this.permissionName,
          "destroy"
        );
      }
    },

    editDisabled() {
      if (this.role === "management") {
        if(this.permissionName === 'list'){
          return (
            !(
              this.userHasManagementPermission('list', "update") ||
              this.userHasManagementPermission('custom_list', "update")
            )
          );
        }else if(this.permissionName === 'third_party_apis_consumables'){
          return !this.userHasManagementPermission(this.permissionName, "edit");
        }
        else{
          return !this.userHasManagementPermission(this.permissionName, "update");
        }
      } else if (this.role === "fibercloud") {
        return !this.userHasFibercloudPermission(this.permissionName, "update");
      }
    }
  },

  methods: {
    /**
     * Emits an event to open the edit page.
     */
    editItem() {
      this.$emit("editItem");
    },

    /**
     * Opens the dialog that allows the user to confirm if he really wants to delete the workspace or not.
     */
    toggleDeleteDialog(value) {
      this.dialog = value;
    },

    /**
     * Emits an event to delete the item.
     */
    deleteItem() {
      this.$emit("deleteItem");
      this.toggleDeleteDialog(false);
    }
  }
};
</script>

<style scoped>
.view.page {
  width: 100%;
  padding-right: 40px;
}

.view.page .title-section {
  width: 100%;
  padding: 35px 0 20px 30px;
}
.view.page .page-title {
  padding-bottom: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.view.page .main-section {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
.view.page .buttons {
  display: flex;
  flex-direction: row;
}
.view.page .button-icon {
  width: 10px;
  height: auto;
  margin-right: 8px;
}
.view.page .button-icon path {
  fill: #515358;
}

.view.page .data-image {
  width: 100%;
  height: 110px;
  border-radius: 2px 2px 0 0;
  object-fit: cover;
}
.view.page .data-fields {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.page-header {
  width: 100%;
  min-height: 90px;
}

img {
  object-fit: contain !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Management/ViewPage/ViewPage.vue